import React, { useCallback, useEffect, useRef, useState } from 'react';
import { App, Button, Image, Modal, Tag, Typography } from 'antd';

import { normalizeId } from '../../utils/ids';
import { formatBytes } from '../../utils/formatters';
import {
	downloadItemAttachment,
	fetchItemAttachmentBlob,
	fetchItemAttachmentFullBlob,
	getFileExtension,
} from '../../services/attachments';
import useAttachmentVariant from '../../hooks/useAttachmentVariant';
import { hasAttachmentVariant, isPhotoAttachment } from '../../utils/attachmentVariants';
import { getFileTypeIcon } from './AttachmentsSection';
import { ARROWS_EXPAND, DOWNLOAD } from '../icons';
import { publicDownloadLines } from '../../utils/publicFiles';
import { isFlaggedFile } from '../../utils/fileScan';
import DialogLoadState from './DialogLoadState';
import { Skeleton } from '../loading';
import FileScanNote, { confirmFlaggedFile } from './FileScanNote';
import { anchorOf } from '../../platform';
import useWindowClass from '../../hooks/useWindowClass';

const { Text } = Typography;

/**
 * What the browser can be trusted to render from a blob, by extension.
 *
 * An ALLOWLIST, not a blocklist: the file came from a user, and the only safe
 * default for "we don't recognise this" is to offer the download instead of
 * handing unknown bytes to a renderer.
 */
const PREVIEW_KINDS_BY_EXT = {
	png: 'image', jpg: 'image', jpeg: 'image', gif: 'image',
	webp: 'image', bmp: 'image', avif: 'image',
	pdf: 'pdf',
	txt: 'text', md: 'text', csv: 'text', json: 'text',
	log: 'text', yml: 'text', yaml: 'text', xml: 'text',
	mp3: 'audio', wav: 'audio', m4a: 'audio', ogg: 'audio',
	mp4: 'video', webm: 'video', mov: 'video',
};

/**
 * Never previewable, whatever the mime says. The API already refuses these at
 * upload, and downloads are attachment-disposition + nosniff — this is the
 * third lock on the same door, because these are the two types that execute
 * script in the origin that renders them.
 */
const NEVER_PREVIEWABLE_EXTS = ['svg', 'html', 'htm'];
const NEVER_PREVIEWABLE_MIMES = ['image/svg+xml', 'text/html', 'application/xhtml+xml'];

// Only consulted when the extension says nothing at all. A recognised
// extension is the answer; a mime is a claim the uploader made.
const PREVIEW_KINDS_BY_MIME_PREFIX = [
	['image/', 'image'],
	['audio/', 'audio'],
	['video/', 'video'],
	['text/', 'text'],
	['application/pdf', 'pdf'],
	['application/json', 'text'],
];

// The type a blob URL must CARRY for the browser to render it inline. A
// blob is typed from the response's Content-Type, and an attachment uploaded
// without one (curl, an API client, some mobile pickers) was stored as
// application/octet-stream — which a frame treats as "save this", so a PDF
// preview opened blank while the file quietly downloaded, nameless. The
// extension is the allowlist's own key, so re-typing the bytes by kind is no
// more trust than the allowlist already extends.
const MIME_BY_KIND = { pdf: 'application/pdf' };

const typedBlobFor = (blob, kind) => {
	const wanted = MIME_BY_KIND[kind];
	if (!wanted || `${blob?.type || ''}`.toLowerCase() === wanted) return blob;
	return new Blob([blob], { type: wanted });
};

// A megabyte of text is already more than anyone reads in a modal, and it is
// the point where a <pre> starts costing real layout time.
const TEXT_PREVIEW_MAX_CHARS = 1024 * 1024;

// Past this, the preview still runs — it just warns first, because the wait is
// a whole file over the wire before anything appears.
const LARGE_PREVIEW_BYTES = 25 * 1024 * 1024;

const extensionOf = (attachment) => {
	const raw = attachment?.ext || getFileExtension(attachment?.originalName) || '';
	return `${raw}`.toLowerCase().replace(/^\./, '');
};

const mimeOf = attachment => `${attachment?.mime || ''}`.toLowerCase().trim();

/**
 * The renderer this attachment gets, or null for "offer the download".
 * Exported shape is deliberately narrow: callers ask `isPreviewableAttachment`.
 */
function getPreviewKind(attachment) {
	if (!attachment) return null;
	const ext = extensionOf(attachment);
	const mime = mimeOf(attachment);

	if (NEVER_PREVIEWABLE_EXTS.includes(ext)) return null;
	if (NEVER_PREVIEWABLE_MIMES.some(blocked => mime.startsWith(blocked))) return null;

	// A photo the server sized (keepr-api PR 10a) opens its 2,048 px `lg`
	// WebP, whatever the original's type — an iPhone HEIC included, which a
	// browser cannot show itself. `image` is null for a PDF, so a PDF's
	// first-page variants never make it a photo: it keeps its frame below.
	if (isPhotoAttachment(attachment) && hasAttachmentVariant(attachment, 'lg')) return 'photo';

	const byExt = PREVIEW_KINDS_BY_EXT[ext];
	if (byExt) return byExt;
	// A known-but-unpreviewable extension (zip, docx, exe…) is settled; only a
	// file with no extension at all falls through to what the mime claims.
	if (ext) return null;

	const match = PREVIEW_KINDS_BY_MIME_PREFIX.find(([prefix]) => mime.startsWith(prefix));
	return match ? match[1] : null;
}

/** Can this attachment be shown in the browser at all? (Used by the tests.) */
export function isPreviewableAttachment(attachment) {
	return getPreviewKind(attachment) !== null;
}

/**
 * Does this attachment's row open the viewer? For a reader who may take
 * copies, every file: the viewer shows what it can and offers Download for
 * the rest. For a reader who may not, only a photo with its `lg` size:
 * looking at it reads the variants route, which every reader of the item may
 * ask, and the viewer then offers neither Download nor the full resolution.
 */
export function canOpenAttachment(attachment, { canDownload = true } = {}) {
	if (!attachment) return false;
	return canDownload || getPreviewKind(attachment) === 'photo';
}

// jsdom (and older Safari) have no Blob.text(); FileReader is the floor.
const readBlobText = (blob) => {
	if (typeof blob?.text === 'function') return blob.text();
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(`${reader.result || ''}`);
		reader.onerror = () => reject(reader.error || new Error('Unable to read that file.'));
		reader.readAsText(blob);
	});
};

// A refusal to a blob request arrives as a Blob, so the server's words
// (copies_not_allowed says why) have to be read out of it to be shown.
async function refusalMessage(error) {
	const data = error?.response?.data;
	try {
		const raw = data instanceof Blob ? await readBlobText(data) : data;
		const body = typeof raw === 'string' ? JSON.parse(raw) : raw;
		if (body && typeof body.message === 'string' && body.message.trim()) return body.message;
	} catch { /* not JSON: the caller's own words */ }
	return null;
}

/**
 * Shows one attachment in the browser, with a Download button that always
 * works even when nothing can be rendered.
 *
 * Two constraints shape this component:
 *
 * 1. The bytes need the JWT, so there is no `<img src="/api/…">` — everything
 *    renders from a blob object URL fetched through the authenticated client
 *    and revoked when the modal closes or moves to another file.
 * 2. A preview is still a copy leaving the server, so it is fetched with
 *    `via: 'preview'` and lands in the transaction log as a preview. Files we
 *    cannot render are never fetched at all: no wasted transfer, and no
 *    preview transaction for something nobody ever saw.
 *
 * A photo the server sized is the exception to 2 (keepr-api PR 10a, owner
 * ruling G10): it opens its 2,048 px `lg` WebP from the variants route — no
 * ledger row, quick on a phone's data plan — through the shared variant
 * cache, and a click on it opens antd's full-screen view with zoom and pan.
 * "View full resolution" fetches the original on demand (`view=full`, gated
 * and logged by the server as a view) into that same full-screen view; it is
 * never saved, and its object URL is revoked when the viewer closes or moves
 * on. Download stays its own, explicit action.
 *
 * `canDownload` false (a reader who may not take copies) leaves only the
 * photo's `lg`: no Download, no full resolution, and nothing else fetched.
 *
 * The malware verdict (PR 10e) is said under the title: "Not checked yet",
 * or for a flagged file the warning line. A flagged file's original is
 * never read to draw it here — a photo still shows its `lg` (keepr's own
 * re-encoded pixels), anything else offers Download — and Download and View
 * full resolution each ask first (components/common/FileScanNote.js).
 */
export default function AttachmentPreviewModal({
	attachment,
	itemId,
	open,
	onClose,
	canDownload = true,
}) {
	const { message, modal } = App.useApp();
	// The viewer takes the whole screen in a compact window (§18), as the icon picker does.
	const { compact: phone } = useWindowClass();

	const attachmentId = normalizeId(attachment?._id);
	const resolvedItemId = normalizeId(itemId);
	const name = attachment?.originalName || 'Attachment';
	const ext = extensionOf(attachment);
	const previewKind = getPreviewKind(attachment);
	// What this reader is shown: everything for one who may take copies; for
	// one who may not, the photo's `lg` alone (the rest reads the download
	// endpoint, which would refuse them).
	// A flagged file's original is not fetched to be drawn (PR 10e): only
	// the photo's `lg`, which keepr re-encoded, still shows.
	const flagged = isFlaggedFile(attachment);
	const kind = previewKind === 'photo' || (canDownload && !flagged) ? previewKind : null;
	const isPhoto = kind === 'photo';

	const [objectUrl, setObjectUrl] = useState(null);
	const [textContent, setTextContent] = useState(null);
	const [truncated, setTruncated] = useState(false);
	const [loading, setLoading] = useState(false);
	const [failed, setFailed] = useState(false);
	// Raised by Retry; a read has answered once `loaded` (so the first render is already the wait).
	const [attempt, setAttempt] = useState(0);
	const [loaded, setLoaded] = useState(false);
	const [downloading, setDownloading] = useState(false);

	const lg = useAttachmentVariant(resolvedItemId, open && isPhoto ? attachment : null, 'lg', { attempt });
	const [fullUrl, setFullUrl] = useState(null);
	const [fullLoading, setFullLoading] = useState(false);
	const [zoomOpen, setZoomOpen] = useState(false);
	// Bumped whenever the viewer closes or moves to another file, so a full
	// resolution that lands late is dropped instead of shown under the wrong
	// title.
	const fullRequest = useRef(0);

	useEffect(() => {
		fullRequest.current += 1;
		setFullUrl(null);
		setFullLoading(false);
		setZoomOpen(false);
	}, [open, attachmentId]);

	// One-shot, like the download anchor's: the next open fetches again.
	useEffect(() => () => {
		if (fullUrl) URL.revokeObjectURL(fullUrl);
	}, [fullUrl]);

	// Load on open, and reset first: never show one file's bytes under another
	// file's title (same rule as the admin activity modal).
	useEffect(() => {
		setObjectUrl(null);
		setTextContent(null);
		setTruncated(false);
		setFailed(false);
		setLoaded(false);
		setLoading(false);

		// A photo shows its sizes (above); only the other kinds read the bytes.
		if (!open || !attachmentId || !resolvedItemId || !kind || kind === 'photo') return undefined;

		let active = true;
		let created = null;
		setLoading(true);

		fetchItemAttachmentBlob(resolvedItemId, attachmentId, { via: 'preview' })
			.then(async (blob) => {
				if (kind === 'text') {
					const raw = await readBlobText(blob);
					if (!active) return;
					setTruncated(raw.length > TEXT_PREVIEW_MAX_CHARS);
					setTextContent(raw.slice(0, TEXT_PREVIEW_MAX_CHARS));
					setLoaded(true);
					return;
				}
				created = URL.createObjectURL(typedBlobFor(blob, kind));
				if (!active) {
					URL.revokeObjectURL(created);
					created = null;
					return;
				}
				setObjectUrl(created);
				setLoaded(true);
			})
			.catch((error) => {
				console.error(error);
				if (active) setFailed(true);
			})
			.finally(() => {
				if (active) setLoading(false);
			});

		return () => {
			active = false;
			// A one-shot handle, like the download anchor's: the next open
			// fetches again rather than holding bytes for a modal nobody has
			// on screen.
			if (created) URL.revokeObjectURL(created);
		};
	}, [open, resolvedItemId, attachmentId, kind, attempt]);

	const handleFullResolution = useCallback(async () => {
		if (fullUrl) {
			setZoomOpen(true);
			return;
		}
		if (!resolvedItemId || !attachmentId || !canDownload) return;
		if (!(await confirmFlaggedFile(modal, attachment, { act: 'view' }))) return;
		fullRequest.current += 1;
		const ticket = fullRequest.current;
		setFullLoading(true);
		try {
			const blob = await fetchItemAttachmentFullBlob(resolvedItemId, attachmentId);
			if (ticket !== fullRequest.current) return;
			setFullUrl(URL.createObjectURL(blob));
			setZoomOpen(true);
		} catch (error) {
			if (ticket !== fullRequest.current) return;
			console.error(error);
			// A refusal (copies_not_allowed) is said in the server's words.
			message.error((await refusalMessage(error)) || 'Unable to load the full resolution.');
		} finally {
			if (ticket === fullRequest.current) setFullLoading(false);
		}
	}, [fullUrl, resolvedItemId, attachmentId, canDownload, message, modal, attachment]);

	const handleDownload = useCallback(async (event) => {
		const anchor = anchorOf(event);
		if (!resolvedItemId || !attachmentId) return;
		if (!(await confirmFlaggedFile(modal, attachment))) return;
		setDownloading(true);
		try {
			await downloadItemAttachment(resolvedItemId, attachmentId, attachment?.originalName, { anchor });
		} catch (error) {
			console.error(error);
			message.error('Unable to download that file.');
		} finally {
			setDownloading(false);
		}
	}, [resolvedItemId, attachmentId, attachment, message, modal]);

	const TypeIcon = getFileTypeIcon(ext);

	const renderPreview = () => {
		if (kind === 'image') {
			return <img className='attachment-preview__image' src={objectUrl} alt={name} />;
		}
		if (kind === 'pdf') {
			// The browser's own viewer, in a frame. A browser without an inline
			// PDF viewer (some phones) shows nothing here; the footer's Download
			// is the way through, and it keeps the file's name.
			return (
				<>
					<iframe className='attachment-preview__frame' title={name} src={objectUrl} />
					<Text type='secondary' className='attachment-preview__note'>
						If the page stays blank, your browser has no built-in PDF viewer — download it to view.
					</Text>
				</>
			);
		}
		if (kind === 'audio') {
			// eslint-disable-next-line jsx-a11y/media-has-caption
			return <audio className='attachment-preview__audio' controls src={objectUrl} />;
		}
		if (kind === 'video') {
			// eslint-disable-next-line jsx-a11y/media-has-caption
			return <video className='attachment-preview__video' controls src={objectUrl} />;
		}
		return (
			<>
				{/* A TEXT NODE, never markup: the whole point of previewing a
				    .md or .xml here is that it is shown, not interpreted. */}
				<pre className='attachment-preview__text'>{textContent}</pre>
				{truncated ? (
					<Text type='secondary' className='attachment-preview__note'>
						Truncated — download for the full file.
					</Text>
				) : null}
			</>
		);
	};

	const renderPhoto = () => {
		// antd's Image: a click (or "View full resolution") opens the
		// full-screen view with zoom, pan and rotate — the original once it
		// has been fetched, the `lg` until then.
		return (
			<DialogLoadState
				loaded={Boolean(lg.url)}
				error={lg.failed}
				errorDetail={canDownload ? 'View the full resolution, or download it.' : undefined}
				label='this photo'
				onRetry={() => setAttempt(n => n + 1)}
				skeleton={<Skeleton variant='chart-frame' />}
				minHeight={280}
			>
			<Image
				rootClassName='attachment-preview__photo'
				className='attachment-preview__image'
				src={lg.url}
				alt={name}
				preview={{
					visible: zoomOpen,
					src: fullUrl || lg.url,
					onVisibleChange: visible => setZoomOpen(visible),
				}}
			/>
			</DialogLoadState>
		);
	};

	const renderBody = () => {
		if (!kind) {
			return (
				<div className='attachment-preview__unsupported'>
					<span className='attachment-preview__unsupported-icon' aria-hidden='true'>
						<TypeIcon />
					</span>
					<Text strong>{name}</Text>
					<Text type='secondary'>
						{flagged && previewKind ? 'A flagged file is not opened here.' : 'This file type can\'t be previewed in the browser.'}
					</Text>
					{canDownload ? <Text type='secondary'>Download it to view.</Text> : null}
				</div>
			);
		}
		if (isPhoto) return renderPhoto();
		return (
			<DialogLoadState
				loaded={loaded}
				loading={loading}
				error={failed}
				errorDetail='Download it to view.'
				label='this file'
				onRetry={() => setAttempt(n => n + 1)}
				skeleton={<Skeleton variant='chart-frame' />}
				minHeight={280}
			>
				{renderPreview()}
			</DialogLoadState>
		);
	};

	const isLarge = kind && Number(attachment?.size) > LARGE_PREVIEW_BYTES;

	const footer = [<Button key='close' onClick={onClose}>Close</Button>];
	if (isPhoto && canDownload) {
		footer.push(
			<Button
				key='full'
				icon={<ARROWS_EXPAND />}
				loading={fullLoading}
				onClick={handleFullResolution}
			>
				View full resolution
			</Button>
		);
	}
	if (canDownload) {
		footer.push(
			<Button
				key='download'
				type='primary'
				icon={<DOWNLOAD />}
				loading={downloading}
				onClick={handleDownload}
			>
				Download
			</Button>
		);
	}

	return (
		<Modal
			open={open}
			onCancel={onClose}
			width={phone ? '100%' : 720}
			centered={!phone}
			destroyOnClose
			className={`attachment-preview${phone ? ' attachment-preview--phone' : ''}`}
			title={
				<span className='attachment-preview__title'>
					<span className='attachment-preview__title-name' title={name}>{name}</span>
					{ext ? <Tag className='attachment-preview__title-ext'>{ext.toUpperCase()}</Tag> : null}
				</span>
			}
			footer={footer}
		>
			<FileScanNote file={attachment} className='attachment-preview__scan' />
			{isLarge && loading ? (
				<Text type='secondary' className='attachment-preview__note'>
					{`${formatBytes(attachment?.size)} — large files can take a moment to load.`}
				</Text>
			) : null}
			{renderBody()}
			{/* A manager's count of what strangers took (PR 10c), when the
			    attachment the viewer opened carries it. */}
			{publicDownloadLines(attachment?.publicDownloads).map((line, index) => (
				<Text key={`${index}-${line}`} type='secondary' className='attachment-preview__note attachment-preview__downloads'>{line}</Text>
			))}
		</Modal>
	);
}
